import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./EditBillingInfo.scss";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getPlan, updatePlan } from "../../../services/PlanService";
import Loader from "../../../components/Loader";
import PriceInputSearchField from "../../components/PriceInputSearchField";
import DescriptionInputField from "../../components/DescriptionInputField";
import { toast } from "sonner";
import CommonUtil from "../../../util/CommonUtils";

const EditBillingInfo = ({ mCode, yCode, planName, ...props }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  const [monthlyPlans, setMonthlyPlans] = useState({
    price: "",
    description: "",
    planOrder: "",
    planCode: "",
    trailPeriod: ""
  });
  const [yearlyPlans, setYearlyPlans] = useState({
    price: "",
    description: "",
    planOrder: "",
    planCode: "",
    trailPeriod: ""
  });

  const [montlyBillingSetupError, setMontlyBillingSetupError] = useState({
    price: "",
    description: ""
  });

  const [yearlyBillingSetupError, setYearlyBillingSetupError] = useState({
    price: "",
    description: "",
  });

  const validateBillingFields = () => {
    if (CommonUtil.isEmptyString(monthlyPlans.price)) {
      setMontlyBillingSetupError({
        ...montlyBillingSetupError,
        price: 'Monthly billing price is required' // t("RAL0800"),
      });
      return false;
    }

    if (Math.abs(monthlyPlans.price) < (Math.abs(yearlyPlans.price) / 12)) {
      setMontlyBillingSetupError({
        ...montlyBillingSetupError,
        price: `Value must be greater than yearly price $(${(yearlyPlans.price / 12).toFixed(2)})`
      });
      return false;
    }
  
    if (Math.abs(monthlyPlans.price) >= Math.abs(yearlyPlans.price)) {
      setYearlyBillingSetupError({
        ...yearlyBillingSetupError,
        price: `Value must be greater than monthly price`
      });
      return false;
    }
    if (CommonUtil.isEmptyString(monthlyPlans.description)) {
      setMontlyBillingSetupError({
        ...montlyBillingSetupError,
        description: "Monthly plan description is required"// t("RAL0803"),
      });
      return false;
    }
    if (monthlyPlans.description?.length > 255) {
      setMontlyBillingSetupError({
        ...montlyBillingSetupError,
        description: "Monthly plan description cannot exceed 255 characters."// t("RAL0801"),
      });
      return false;
    }
    if (CommonUtil.isEmptyString(yearlyPlans.price)) {
      setYearlyBillingSetupError({
        ...yearlyBillingSetupError,
        price: "Yearly billing price is required" // t("RAL0805"),
      });
      return false;
    }
    if (CommonUtil.isEmptyString(yearlyPlans.description)) {
      setYearlyBillingSetupError({
        ...yearlyBillingSetupError,
        description: "Yearly plan description is required"// t("RAL0804"),
      });
      return false;
    }
    if (yearlyPlans.description?.length > 255) {
      setYearlyBillingSetupError({
        ...yearlyBillingSetupError,
        description: "Yearly plan description cannot exceed 255 characters." // t("RAL0802"),
      });
      return false;
    }

    return true;
  };

  useEffect(() => {
    loadPlans();
  }, [planName]);

  const loadPlans = () => {
    setIsLoading(true);
    getPlan(planName)
      .then((res) => {
        if (res?.data?.code === "RCI0000") {

          const plansDetails = res.data.data.plansDetails;

          const monthly = plansDetails.filter(
            (plan) => plan.intervalUnit === "months"
          );
          const yearly = plansDetails.filter(
            (plan) => plan.intervalUnit === "years"
          );

          setMonthlyPlans({
            price: monthly[0].price,
            description: monthly[0].description,
            planOrder: monthly[0].planOrder,
            planCode: monthly[0].planCode,
            trailPeriod: monthly[0].trailPeriod
          });
          setYearlyPlans({
            price: yearly[0].price,
            description: yearly[0].description,
            planOrder: yearly[0].planOrder,
            planCode: yearly[0].planCode,
            trailPeriod: yearly[0].trailPeriod
          });
        }
      })
      .catch((error) => {
        console.error("Error loading plans:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleMonthlyPriceChange = (price) => {

    setMonthlyPlans({
      ...monthlyPlans,
      price: price
    });
    setMontlyBillingSetupError({
      ...montlyBillingSetupError,
      price: "",
    });

  };

  const handleYearlyPriceChange = (price) => {
    setYearlyPlans({
      ...yearlyPlans,
      price: price
    });
    setYearlyBillingSetupError({
      ...yearlyBillingSetupError,
      price: "", //t("RAL0805")
    });
  };

  const handleMonthlyDescriptionChange = (description) => {
    setMonthlyPlans({
      ...monthlyPlans,
      description: description
    });

    setMontlyBillingSetupError({
      ...montlyBillingSetupError,
      description: "",
    });
  };

  const handleYearlyDescriptionChange = (description) => {
    setYearlyPlans({
      ...yearlyPlans,
      description: description
    });

    setYearlyBillingSetupError({
      ...yearlyBillingSetupError,
      description: "",
    });

  };

  const handleSave = () => {
    if (!validateBillingFields()) {
      return;
    }
    setIsLoading(true);
    const payload = {
      plan_name: localStorage.getItem("plan_name"),
      plan_order: monthlyPlans.planOrder,
      trail_period: monthlyPlans.trailPeriod,
      update_plan: [
        {
          description: monthlyPlans.description,
          interval_unit: "months",
          interval: 1,
          price: monthlyPlans.price,
        },
        {
          description: yearlyPlans.description,
          interval_unit: 'years',
          interval: 1,
          price: yearlyPlans.price,
        }
      ],
    };

    const monthlyCode = monthlyPlans?.planCode;
    const yearlyCode = yearlyPlans?.planCode;

    updatePlan(monthlyCode, yearlyCode, payload)
      .then((res) => {
        if (res?.data?.code === "RCI0045") {
          toast.success(res.data.message);
          props.onClose(false);
          props.onUpdate();
        } else {
          toast.error(res.data.message);
        }
      })
      .catch((error) => {
        console.error("Error updating plans:", error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleClose = () => {
    props.onClose(false);
  };

  return (
    <Dialog
      className="MuiDialog-paper"
      open={props.open}
      sx={{
        "& .MuiDialog-paper": {
          width: "80% !important",
          maxWidth: "65% !important",
        },
      }}
    >
      <div className="containerbilling">
        <DialogTitle
          className="dailogbox-container"
        >
          {/* {t("RAL0814")} */}
          Edit
          <p
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "300px",
              margin:"0 7px",
              font:"inherit",
              textAlign:"center"
            }}
          >
             {localStorage.getItem("plan_name")}  
          </p>
          billing
        </DialogTitle>

        <Divider className="dailogbox-divider" />

        <DialogContent>
          {isLoading && <Loader open={isLoading} />}

          {/* Use a Grid container to align left and right sections */}
          <Grid container spacing={2}>
            {/* Left Section for Monthly Plans */}
            <Grid item xs={6}>
              <Box className="editBillingplansLeft">
                <Box>
                  <Grid container spacing={1}>
                    <Grid item xs={12} display={"flex"}>
                      <Grid xs={6}>
                        <Typography className="editbillinglabel">
                          {/* {t("RAL0789")} */}
                          Plan ID
                        </Typography>
                      </Grid>
                      <Grid xs={6}>
                        <Typography className="editbillinglabel">
                          {monthlyPlans.planCode}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} display={"flex"}>
                      <Grid xs={6}>
                        <Typography className="editbillinglabel2">
                          {/* {t("RAL0815")} */}
                          Billing cycle
                        </Typography>
                      </Grid>
                      <Grid xs={6}>
                        <Typography className="editbillinglabel2">
                          {/* {plan.intervalUnit} */}
                          {/* {t("RAL0792")} */}
                          Monthly
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid className="dailogbox-price" item xs={12}  >
                      <Grid xs={12} sx={{}}>
                        <div className="editbillinglabel3">Price<span style={{ color: "#FE5050" }}>*</span></div>
                        <PriceInputSearchField
                          // label={t("RAL0816")}
                          label={"Enter amount"}
                          value={monthlyPlans.price || ""}
                          onChange={handleMonthlyPriceChange}
                          error={montlyBillingSetupError.price}
                          helperText={montlyBillingSetupError.price}
                        />
                      </Grid>
                    </Grid>

                    <Grid item xs={12} className="dailogbox-description" >
                      <Grid xs={12} >
                        <div className="editbillinglabel3">Plan description<span style={{ color: "#FE5050" }}>*</span></div>
                        <DescriptionInputField
                          // label={t("RAL0817")}
                          label={"Type message"}
                          value={monthlyPlans.description || ""}
                          onChange={handleMonthlyDescriptionChange}
                          error={montlyBillingSetupError.description}
                          helperText={montlyBillingSetupError.description}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>

            {/* Vertical Line */}
            <Grid item xs={0.5} className="editbillingverticle" >
              <div className="billing-vertical-line"></div>
            </Grid>

            {/* Right Section for Yearly Plans */}
            <Grid item xs={5.5}>
              <Box className="editBillingplansRight" >
                <Box>
                  <Grid container spacing={1}>
                    <Grid item xs={12} display={"flex"}>
                      <Grid xs={6}>
                        <Typography className="editbillinglabel">
                          {/* {t("RAL0789")} */}
                          Plan ID
                        </Typography>
                      </Grid>
                      <Grid xs={6}>
                        <Typography className="editbillinglabel">
                          {yearlyPlans.planCode}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} display={"flex"}>
                      <Grid xs={6}>
                        <Typography className="editbillinglabel2">
                          {/* {t("RAL0815")} */}
                          Billing cycle
                        </Typography>
                      </Grid>
                      <Grid xs={6}>
                        <Typography className="editbillinglabel2">
                          {/* {plan.intervalUnit} */}
                          {/* {t("RAL0793")} */}
                          Yearly
                        </Typography>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} className="dailogbox-price" >
                      <Grid xs={12} >
                        <div className="label">Price
                          <span style={{ color: "#FE5050" }}>*</span>
                          <span style={{ marginLeft: "10px" }}>Average month price is
                            <span style={{
                              marginLeft: "10px", fontWeight: "bold",
                              color: (montlyBillingSetupError.price !== "" && !CommonUtil.isEmpty(monthlyPlans.price)) && "#FE5050"
                            }}>${(yearlyPlans.price / 12).toFixed(2)}</span></span>
                        </div>
                        <PriceInputSearchField
                          // label={t("RAL0816")}
                          label={"Enter amount"}
                          value={yearlyPlans.price || ""}
                          onChange={handleYearlyPriceChange}
                          error={yearlyBillingSetupError.price}
                          helperText={yearlyBillingSetupError.price}
                        />
                      </Grid>
                    </Grid>

                    <Grid item xs={12} className="dailogbox-description">
                      <Grid xs={12} >
                        <div className="editbillinglabel3">Plan description<span style={{ color: "#FE5050" }}>*</span></div>
                        <DescriptionInputField
                          // label={t("RAL0817")}
                          label={"Type message"}
                          value={yearlyPlans.description || ""}
                          onChange={handleYearlyDescriptionChange}
                          error={yearlyBillingSetupError.description}
                          helperText={yearlyBillingSetupError.description}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          className="editbillingbuttons"
        >
          <Box>
            <button className={"billing-prevedit-button"} onClick={handleClose}>
              {/* {t("RAL0177")} */}
              Cancel
            </button>
            <button className="billing-nextedit-button" onClick={handleSave}>
              {/* {t("RAL0309")}  */}
              Save
            </button>
          </Box>
        </DialogActions>
      </div>
    </Dialog>
  );
};

export default EditBillingInfo;
